import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, CheckCircle, XCircle } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const PaymentsTab = () => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchClaims = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/payments/claims/pending`);
      setClaims(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchClaims(); }, [fetchClaims]);

  const handleReview = async (claimId, action) => {
    try {
      const res = await axios.post(`${API}/api/payments/claims/${claimId}/review`, { action });
      toast.success(action === 'approve' ? `Approved! ${res.data.tokens_credited} tokens credited.` : 'Claim rejected.');
      fetchClaims();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  if (loading) return <div className="text-center py-12 text-slate-500">Loading claims...</div>;

  return (
    <div data-testid="payments-tab">
      <h2 className="text-lg font-semibold text-white mb-4">Pending Payment Claims</h2>
      {claims.length === 0 ? (
        <div className="text-center py-12 bg-slate-900/50 border border-white/5 rounded-2xl">
          <CreditCard className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500">No pending claims</p>
        </div>
      ) : (
        <div className="space-y-3">
          {claims.map(c => (
            <div key={c.claim_id} className="bg-slate-900/80 border border-white/10 rounded-xl p-4" data-testid={`claim-${c.claim_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-white font-semibold">{c.display_name || c.user_email}</p>
                  <p className="text-slate-500 text-sm">{c.user_email}</p>
                </div>
                <div className="text-right">
                  <p className="text-amber-400 font-bold">{c.tokens} tokens — ${c.amount}</p>
                  <p className="text-slate-500 text-xs">{c.method}</p>
                </div>
              </div>
              <div className="bg-black/30 rounded-lg p-3 mb-3">
                <p className="text-xs text-slate-500 mb-1">Reference / Confirmation</p>
                <p className="text-white font-mono text-sm">{c.reference}</p>
                {c.sender_name && <p className="text-slate-400 text-xs mt-1">Sender: {c.sender_name}</p>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleReview(c.claim_id, 'approve')}
                  className="flex-1 py-2 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors flex items-center justify-center gap-1"
                  data-testid={`approve-${c.claim_id}`}>
                  <CheckCircle className="w-4 h-4" /> Approve
                </button>
                <button onClick={() => handleReview(c.claim_id, 'reject')}
                  className="flex-1 py-2 bg-red-500/20 text-red-400 rounded-lg font-semibold hover:bg-red-500/30 transition-colors flex items-center justify-center gap-1"
                  data-testid={`reject-${c.claim_id}`}>
                  <XCircle className="w-4 h-4" /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PaymentsTab;
